import React from 'react';

import {AnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import {AnalysisViewGraphQL} from 'analysis-tool/AnalysisTypes';
import {assert} from 'Asserts';
import {assertAndContinue} from 'Asserts';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import StepConfigForm from 'pipelines/generalized-step-config-editor/editors/StepConfigForm';
import {STEP_CONFIG_SETTINGS_V2} from 'pipelines/generalized-step-config-editor/step-config-settings/StepConfigSettings';
import {setValueInConfig} from 'pipelines/generalized-step-config-editor/step-config-settings/StepConfigSettingsUtils';
import {
  stepTypeIsConfigEditorSupported,
  isUiSchemaForStepTypeV2,
} from 'pipelines/generalized-step-config-editor/stepTypeIsConfigEditorSupported';
import {PipelineStep} from 'pipelines/PipelineTypes';
import {assertPipelineStepIsStepType} from 'pipelines/PipelineTypes';
import {getStepTypeConfigShape} from 'pipelines/StepShapeUtils';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {JSONValue} from 'util/Types';

import useMaybeDraftStep from '../useMaybeDraftStep';

interface EditViewChartProps {
  analysis: AnalysisGraphQL;
  clientStateId: string;
  step: PipelineStep<PipelineStepType.CHART_VIEW, 0>;
  view: AnalysisViewGraphQL;
}

export default function EditViewChart({
  analysis,
  clientStateId,
  step,
  view,
}: EditViewChartProps): JSX.Element {
  // We only support updating Charts that are outputs of CHART_VIEW steps.
  const {stepType, stepVersion} = step;

  // Store a whole step as state, rather than just `draftConfig`, so the draft config does not go out-of-sync
  // with `stepType` and `inputStepDatas` during async updates.
  // TODO(BNCH-121407): Clear the draft step when the step is deleted, and update it to work for multiple tabs.
  const {draftStep, setDraftStep} = useMaybeDraftStep(step.id, {...step, config: {...step.config}});
  assert(draftStep != null, 'Expected draft step to be non-null');
  assertPipelineStepIsStepType(PipelineStepType.CHART_VIEW, 0, draftStep);

  if (!stepTypeIsConfigEditorSupported(stepType, stepVersion)) {
    assertAndContinue(false, `StepConfigEditorPane used for ${stepType} v${stepVersion}`);
    return <InlineBanner bannerType={InlineBannerType.WARNING} text="No configuration is available." />;
  }

  assert(isUiSchemaForStepTypeV2(stepType), 'Expected step type to be supported in StepConfigForm', {
    stepType,
    stepVersion,
  });
  const {uiSchema} = STEP_CONFIG_SETTINGS_V2[stepType][stepVersion];

  return (
    <StepConfigForm
      config={draftStep.config ?? {}}
      configShape={getStepTypeConfigShape(stepType, stepVersion)}
      clientStateId={clientStateId}
      onChangeConfig={(
        config: Record<string, JSONValue | null>,
        path: Array<string>,
        newValue: JSONValue
      ) => {
        setDraftStep({...draftStep, config: setValueInConfig(config ?? {}, path, newValue)});
      }}
      uiSchema={uiSchema}
    />
  );
}
